import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  FilterInvoiceChargeDomainMappingReq,
  PaginationRequest,
  ResponseDtoInvoiceChargeDomainMappingMetadataDto,
  ResponseDtoPageInvoiceChargeDomainMappingDto,
  UpsertInvoiceChargeDomainMappingReq,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

/**
 * Interface for pagination parameters
 */
export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  searchQuery?: string;
}

/**
 * Interface for invoice charge domain mapping filters
 */
export interface InvoiceChargeDomainMappingFilters {
  serviceProviderCodes?: string[];
  invoiceChargeNames?: string[];
  chargeDomains?: string[];
  chargeSubDomains?: string[];
  legTypes?: string[];
  contractChargeUnits?: string[];
}

/**
 * Hook to fetch invoice charge domain mappings by service provider code with pagination, sorting and search

 * @param paginationParams Pagination, sorting and search parameters
* @param filters Filter parameters for invoice charge domain mappings
 * @returns Query result with invoice charge domain mappings data
 */
export const useInvoiceChargeDomainMappings = (
  paginationParams?: PaginationParams,
  filters?: InvoiceChargeDomainMappingFilters
): UseQueryResult<ResponseDtoPageInvoiceChargeDomainMappingDto> =>
  useQuery(['invoiceChargeDomainMappings', paginationParams, filters], async () => {
    // Create pagination request from params
    const paginationRequest: PaginationRequest = {
      page: paginationParams?.page ?? 0, // API uses 0-based indexing
      size: paginationParams?.pageSize ?? 10,
      sortingColum: paginationParams?.sortBy,
      sortingDirection: paginationParams?.sortOrder === 'desc' ? 'DESC' : 'ASC',
    };

    // Create filter request
    const filterRequest: FilterInvoiceChargeDomainMappingReq = {
      paginationRequest,
      // Add type-safe filters if provided
      ...(filters?.serviceProviderCodes && { serviceProviderCodes: filters.serviceProviderCodes }),
      ...(filters?.invoiceChargeNames && { invoiceChargeNames: filters.invoiceChargeNames }),
      ...(filters?.chargeDomains && { chargeDomains: filters.chargeDomains as any }),
      ...(filters?.chargeSubDomains && { chargeSubDomains: filters.chargeSubDomains as any }),
      ...(filters?.legTypes && { legTypes: filters.legTypes as any }),
      ...(filters?.contractChargeUnits && { contractChargeUnits: filters.contractChargeUnits as any }),
      // Add search parameters if provided
      ...(paginationParams?.searchQuery && {
        invoiceChargeName: paginationParams.searchQuery,
        // You can add more search fields here if needed
      }),
    };

    // Fetch data from the API with server-side pagination and search
    const response = await tFinancialService.api.getInvoiceChargeDomainMappings(filterRequest);
    return response.data;
  });

/**
 * Hook to create or update invoice charge domain mappings
 * @returns Mutation function for upserting invoice charge domain mappings
 */
export const useUpsertInvoiceChargeDomainMapping = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (mapping: UpsertInvoiceChargeDomainMappingReq | UpsertInvoiceChargeDomainMappingReq[]) => {
      // Convert single mapping to array if needed
      const mappings = Array.isArray(mapping) ? mapping : [mapping];
      const response = await tFinancialService.api.upsertInvoiceChargeDomainMapping(mappings);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Get the service provider ID from the variables to determine which queries to invalidate
        const serviceProviderId = Array.isArray(variables) ? variables[0]?.serviceProviderId : variables.serviceProviderId;

        // Invalidate relevant queries to refetch data
        if (serviceProviderId) {
          // Find all queries that match the service provider ID
          queryClient.invalidateQueries({
            predicate: (query) => {
              if (Array.isArray(query.queryKey) && query.queryKey[0] === 'invoiceChargeDomainMappings') {
                // If there's a service provider code in the query key, check if it matches
                return true;
              }
              return false;
            },
          });
        } else {
          // If no specific service provider ID, invalidate all invoice charge domain mapping queries
          queryClient.invalidateQueries(['invoiceChargeDomainMappings']);
        }
      },
    }
  );
};

/**
 * Hook to delete invoice charge domain mappings by IDs
 * @returns Mutation function for deleting invoice charge domain mappings
 */
/**
 * Hook to fetch invoice charge domain mapping metadata for filter options
 * @param filters Filter parameters for invoice charge domain mappings
 * @returns Query result with invoice charge domain mapping metadata
 */
export const useInvoiceChargeDomainMappingMetadata = (
  filters?: InvoiceChargeDomainMappingFilters
): UseQueryResult<ResponseDtoInvoiceChargeDomainMappingMetadataDto> =>
  useQuery(['invoiceChargeDomainMappingMetadata', filters], async () => {
    // Create filter request with empty pagination to satisfy the type requirement
    const filterRequest: FilterInvoiceChargeDomainMappingReq = {
      paginationRequest: {
        page: 0,
        size: 10,
      },
      // Add type-safe filters if provided
      ...(filters?.serviceProviderCodes && { serviceProviderCodes: filters.serviceProviderCodes }),
      ...(filters?.invoiceChargeNames && { invoiceChargeNames: filters.invoiceChargeNames }),
      ...(filters?.chargeDomains && { chargeDomains: filters.chargeDomains as any }),
      ...(filters?.chargeSubDomains && { chargeSubDomains: filters.chargeSubDomains as any }),
      ...(filters?.legTypes && { legTypes: filters.legTypes as any }),
      ...(filters?.contractChargeUnits && { contractChargeUnits: filters.contractChargeUnits as any }),
    };

    // Fetch metadata from the API
    const response = await tFinancialService.api.getInvoiceChargeDomainMappingMetadata(filterRequest);
    return response.data;
  });
export const useDeleteInvoiceChargeDomainMappings = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: { ids: number[]; serviceProviderCode?: string }) => {
      const response = await tFinancialService.api.deleteInvoiceChargeDomainMappingByIds(params.ids);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate relevant queries to refetch data
        if (variables.serviceProviderCode) {
          queryClient.invalidateQueries(['invoiceChargeDomainMappings', variables.serviceProviderCode]);
        } else {
          // If no specific service provider code, invalidate all invoice charge domain mapping queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              if (Array.isArray(query.queryKey) && query.queryKey[0] === 'invoiceChargeDomainMappings') {
                return true;
              }
              return false;
            },
          });
        }
      },
    }
  );
};
